import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthenticatedUser, AuthTokens, LoginRequest } from '@/lib/shared-types';
import { api, tokenStore } from '@/lib/api';

interface AuthState {
  user: AuthenticatedUser | null;
  status: 'idle' | 'loading' | 'authenticated' | 'error';
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  status: 'idle',
  error: null,
};

export const login = createAsyncThunk<AuthenticatedUser, LoginRequest>(
  'auth/login',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post<AuthTokens & { user: AuthenticatedUser }>(
        '/auth/login',
        payload,
      );
      tokenStore.set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
      return data.user;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message ?? 'Login failed');
    }
  },
);

export const fetchMe = createAsyncThunk<AuthenticatedUser | null>('auth/me', async () => {
  if (!tokenStore.get()) return null;
  const { data } = await api.get<AuthenticatedUser>('/auth/me');
  return data;
});

export const logout = createAsyncThunk<void>('auth/logout', async () => {
  try {
    await api.post('/auth/logout');
  } finally {
    tokenStore.set(null);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<AuthenticatedUser>) => {
        state.status = 'authenticated';
        state.user = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'error';
        state.error = String(action.payload ?? 'Login failed');
      })
      .addCase(fetchMe.fulfilled, (state, action) => {
        state.user = action.payload;
        state.status = action.payload ? 'authenticated' : 'idle';
      })
      .addCase(fetchMe.rejected, (state) => {
        state.status = 'idle';
        state.user = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.status = 'idle';
      });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
